'use client';

import React, { createContext, useContext } from 'react';
import { useQuery } from 'react-query';
import {
  LEARN_AB_TEST_DEFAULT,
  LearnABTestData,
} from '@osmosis/shared/constants/learnAbTests';
import { useFetch } from '@osmosis/nextjs-components/context/FetchProvider';
import { useUserContext } from '@osmosis/nextjs-components/context/UserContext';
import {
  FeatureFlagsSet,
  useFeatureFlagsContext,
} from '@osmosis/nextjs-components/context/FeatureFlagsContext';
import { UUID_COOKIE } from '@osmosis/shared/constants/cookies';
import useCookie from '../hooks/useCookie';

export type { LearnABTestData };

type LearnABTestContextProps = {
  abTestData: LearnABTestData | null;
  isResolved: boolean;
};

const LearnABTestContext = createContext<LearnABTestContextProps>({
  abTestData: null,
  isResolved: false,
});

/**
 * Hook to read the resolved learn-page A/B test assignments.
 */
export const useLearnABTest = (): LearnABTestContextProps =>
  useContext(LearnABTestContext);

type LearnABTestProviderProps = {
  children: React.ReactNode;
};

/**
 * Resolves learn-page A/B test assignments client-side. Learn pages are
 * Cloudflare-cached, so assignment must happen via an API call on mount, not
 * SSR.
 *
 * Fetching is gated behind the `learn-ab-test` feature flag — the master
 * switch for whether the learn-page A/B test framework runs at all. No
 * specific test is active right now (the prior redesign and OAI placement
 * tests both concluded), so flipping the flag on today just exercises the
 * dormant scaffolding (the API route returns the default with no fields). To
 * add a test:
 * 1. Add a resolver to `app/_lib/backend/learn/abTests.ts` and wire it into
 * `app/api/learnPageABTest/route.page.ts`.
 * 2. Add the resolved field to `LearnABTestData` in
 * `libs/shared/src/constants/learnAbTests.ts`.
 * 3. Turn the `learn-ab-test` flag on (DB/admin-managed).
 *
 * Bucketing stays sticky either way:
 * - Logged-in users are bucketed by `userID`, so the request fires as soon as the user is loaded — no uuid cookie required.
 * - Logged-out users are bucketed by the `uuid` cookie, so we wait for it to be present (reactively) before firing; if it never appears, fall back to the default.
 */
export const LearnABTestProvider = ({
  children,
}: LearnABTestProviderProps): JSX.Element => {
  const { get } = useFetch();
  const { isUserLoaded, user } = useUserContext();
  const { isActive } = useFeatureFlagsContext();

  const isLoggedIn = !!user?.userID;

  // Master gate for the whole learn-page A/B test framework, independent of
  // any specific test.
  const learnABTestEnabled = isActive(FeatureFlagsSet.learnABTest);

  // Only logged-out users depend on the uuid cookie; watch for it reactively.
  const watchUuid = isUserLoaded && !isLoggedIn;
  const { hasCookie: hasUuidCookie, timedOut } = useCookie({
    cookieName: UUID_COOKIE,
    watch: watchUuid,
  });

  const enabled =
    isUserLoaded && (isLoggedIn || hasUuidCookie) && learnABTestEnabled;

  const {
    data = null,
    isLoading,
    isError,
  } = useQuery(
    ['learnPageABTest'],
    async () => {
      const { data } = await get<LearnABTestData>('/api/learnPageABTest');
      return data;
    },
    {
      refetchOnWindowFocus: false,
      enabled,
    },
  );

  // A logged-out user whose uuid cookie never appeared cannot be bucketed;
  // fall back to the default.
  const skippedNoUuid = watchUuid && !hasUuidCookie && timedOut;

  const isResolved = enabled && !isLoading;

  const abTestData =
    isError || skippedNoUuid
      ? LEARN_AB_TEST_DEFAULT
      : (data ?? LEARN_AB_TEST_DEFAULT);

  return (
    <LearnABTestContext.Provider
      value={{
        abTestData,
        isResolved,
      }}
    >
      {children}
    </LearnABTestContext.Provider>
  );
};

export default LearnABTestProvider;
